import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Chip, CircularProgress, Alert, TextField, MenuItem,
  Stack, Tabs, Tab, Tooltip, TablePagination, InputAdornment, Button,
  Avatar, Divider, alpha, useTheme, useMediaQuery, Grid, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Search, Phone, MessageCircle, ClipboardList, RefreshCw, Download, RotateCcw,
  Building2, Clock, AlertTriangle, Users, TrendingUp,
  CheckCircle2, Copy, Check, Play, ArrowRight, Eye, UserX, Trash2, MoreVertical,
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  fetchFollowUps, exportFollowUpsCsv, logFollowUpContact, removeFollowUp, deleteFollowUpUser, SEGMENTS, SEGMENT_LABEL, SEGMENT_COLORS,
  FOLLOWUP_STATUSES, STATUS_COLORS, FOLLOWUP_REASONS, DUE_FILTERS, SORT_OPTIONS, labelOf,
} from '../utils/followUpApi';
import FollowUpModal from '../components/FollowUpModal';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import { waApi } from '../components/whatsapp/waApi';
import OnboardingAutoReminders from '../components/OnboardingAutoReminders';

const ORANGE = '#FE5F01';

const waNumber = (phone) => {
  let d = String(phone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = d.replace(/^0+/, '');
  return d.length === 10 ? `91${d}` : d;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(d);
  }
};

const getInitials = (name) => {
  if (!name || name === '—') return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0][0].toUpperCase();
};

export default function FollowUpsView() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSegment = (() => {
    const q = searchParams.get('segment');
    return SEGMENTS.some((s) => s.key === q) ? q : 'all';
  })();

  const [segment, setSegment] = useState(initialSegment);
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [reasonCategory, setReasonCategory] = useState('');
  const [due, setDue] = useState(searchParams.get('due') || '');
  const [sort, setSort] = useState('created_desc');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, unverified: 0, business_pending: 0, product_pending: 0 });
  const [kpi, setKpi] = useState({ total: 0, pending: 0, in_progress: 0, converted: 0, overdue: 0, due_today: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [rowMenu, setRowMenu] = useState({ anchor: null, user: null });
  const closeRowMenu = () => setRowMenu({ anchor: null, user: null });
  const [exporting, setExporting] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(null);

  // WhatsApp Follow-up Automation Modal state
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waAutoStatus, setWaAutoStatus] = useState(null);
  const [waSegment, setWaSegment] = useState('all');
  const [waDryRun, setWaDryRun] = useState(false);
  const [waLimit, setWaLimit] = useState(50);
  const [waRunning, setWaRunning] = useState(false);
  const [waRunReport, setWaRunReport] = useState(null);

  const handleOpenWaModal = async () => {
    setWaSegment(segment !== 'all' ? segment : 'all');
    setWaRunReport(null);
    setWaModalOpen(true);
    try {
      const data = await waApi.getFollowUpAutomationStatus();
      setWaAutoStatus(data);
    } catch {
      // ignore
    }
  };

  const handleExecuteWaRun = async () => {
    setWaRunning(true);
    try {
      const { report } = await waApi.runFollowUpAutomation({
        segment: waSegment,
        dry_run: waDryRun,
        limit: Number(waLimit) || 50,
      });
      setWaRunReport(report);
      // Refresh CRM records and counters
      load();
    } catch (e) {
      alert(e?.response?.data?.msg || 'Failed to execute WhatsApp follow-up run');
    } finally {
      setWaRunning(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchFollowUps({
        segment,
        status: status || undefined,
        reason_category: reasonCategory || undefined,
        due: due || undefined,
        sort: sort || undefined,
        search: debounced || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(data.users || []);
      if (data.counts) setCounts(data.counts);
      if (data.kpi) setKpi(data.kpi);
      setTotal(data.totalUsers || 0);
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not load the follow-up list.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [segment, status, reasonCategory, due, sort, debounced, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const badge = (key) => (key === 'all' ? counts.all : counts[key]) ?? 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFollowUpsCsv({
        segment,
        status: status || undefined,
        reason_category: reasonCategory || undefined,
        due: due || undefined,
        search: debounced || undefined,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `follow-ups_${segment}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatus('');
    setReasonCategory('');
    setDue('');
    setSort('created_desc');
    setPage(0);
    setSearchParams(segment === 'all' ? {} : { segment }, { replace: true });
  };

  const handleQuickRemove = async (u) => {
    const personName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.business_name || 'this lead';
    if (!window.confirm(`Are you sure you want to remove ${personName} from the follow-up pipeline?`)) return;
    try {
      await removeFollowUp(u._id);
      load();
    } catch (e) {
      alert(e?.response?.data?.msg || 'Could not remove lead from follow-up.');
    }
  };

  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const handleConfirmDeleteUser = async () => {
    if (!deleteTargetUser) return;
    setDeletingUser(true);
    try {
      await deleteFollowUpUser(deleteTargetUser._id);
      setDeleteTargetUser(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.msg || 'Could not delete user from database.');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleKpiCardClick = (kpiFilter) => {
    if (kpiFilter === 'pending') {
      setStatus('pending');
      setDue('');
    } else if (kpiFilter === 'in_progress') {
      setStatus('in_progress');
      setDue('');
    } else if (kpiFilter === 'overdue') {
      setStatus('');
      setDue('overdue');
    } else if (kpiFilter === 'converted') {
      setStatus('converted');
      setDue('');
    } else if (kpiFilter === 'all') {
      setStatus('');
      setDue('');
    }
    setPage(0);
  };

  const handleCopyPhone = (phone) => {
    if (phone) {
      navigator.clipboard.writeText(String(phone));
      setCopiedPhone(phone);
      setTimeout(() => setCopiedPhone(null), 2000);
    }
  };

  const activeFiltersCount =
    (status ? 1 : 0) + (reasonCategory ? 1 : 0) + (due ? 1 : 0) + (debounced ? 1 : 0);

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Follow-ups CRM"
        subtitle="Manage onboarding pipeline: reach out to unverified users, pending business setups, and catalog additions."
        count={total}
        countLabel="leads"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Commerce', path: '/users' },
          { label: 'Follow-ups' },
        ]}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<MessageCircle size={16} />}
              onClick={handleOpenWaModal}
              sx={{
                fontWeight: 700,
                bgcolor: '#25D366',
                color: '#fff',
                '&:hover': { bgcolor: '#128C7E' },
              }}
            >
              WhatsApp Automation
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
              disabled={exporting || total === 0}
              onClick={handleExport}
              sx={{ fontWeight: 600 }}
            >
              Export CSV
            </Button>
          </Stack>
        }
        secondaryAction={
          <Tooltip title="Refresh data">
            <IconButton aria-label="Refresh data" onClick={load} size="small">
              <RefreshCw size={18} />
            </IconButton>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Automatic onboarding reminders (WhatsApp + email, then hand-off to this list) */}
      <OnboardingAutoReminders onChanged={load} />

      {/* Interactive KPI Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Paper
            variant="outlined"
            onClick={() => handleKpiCardClick('all')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'all 0.2s',
              bgcolor: (!status && !due) ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              borderColor: (!status && !due) ? 'primary.main' : 'divider',
              '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Total in Funnel
              </Typography>
              <Users size={18} color="#637381" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: 'text.primary' }}>
              {counts.all || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Requires onboarding setup
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Paper
            variant="outlined"
            onClick={() => handleKpiCardClick('pending')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'all 0.2s',
              bgcolor: status === 'pending' ? alpha(theme.palette.warning.main, 0.08) : 'background.paper',
              borderColor: status === 'pending' ? 'warning.main' : 'divider',
              '&:hover': { borderColor: 'warning.main', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', textTransform: 'uppercase' }}>
                Requires Action
              </Typography>
              <Clock size={18} color="#ed6c02" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: 'warning.dark' }}>
              {kpi.pending || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fresh / uncontacted leads
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Paper
            variant="outlined"
            onClick={() => handleKpiCardClick('in_progress')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'all 0.2s',
              bgcolor: status === 'in_progress' ? alpha(theme.palette.info.main, 0.08) : 'background.paper',
              borderColor: status === 'in_progress' ? 'info.main' : 'divider',
              '&:hover': { borderColor: 'info.main', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.dark', textTransform: 'uppercase' }}>
                In Progress
              </Typography>
              <TrendingUp size={18} color="#0288d1" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: 'info.dark' }}>
              {kpi.in_progress || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active touchpoints & nurturing
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Paper
            variant="outlined"
            onClick={() => handleKpiCardClick('overdue')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'all 0.2s',
              bgcolor: due === 'overdue' ? alpha(theme.palette.error.main, 0.08) : 'background.paper',
              borderColor: due === 'overdue' ? 'error.main' : 'divider',
              '&:hover': { borderColor: 'error.main', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase' }}>
                Follow-up Due
              </Typography>
              <AlertTriangle size={18} color="#d32f2f" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: 'error.main' }}>
              {(kpi.overdue || 0) + (kpi.due_today || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {kpi.overdue || 0} overdue · {kpi.due_today || 0} today
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Paper
            variant="outlined"
            onClick={() => handleKpiCardClick('converted')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'all 0.2s',
              bgcolor: status === 'converted' ? alpha(theme.palette.success.main, 0.08) : 'background.paper',
              borderColor: status === 'converted' ? 'success.main' : 'divider',
              '&:hover': { borderColor: 'success.main', transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark', textTransform: 'uppercase' }}>
                Converted
              </Typography>
              <CheckCircle2 size={18} color="#2e7d32" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color: 'success.dark' }}>
              {kpi.converted || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Successfully activated
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Container Card */}
      <Card sx={{ overflow: 'hidden', borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {/* Segment Filter Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.grey[500], 0.02) }}>
          <Tabs
            value={segment}
            onChange={(e, v) => {
              setSegment(v);
              setPage(0);
              setSearchParams(v === 'all' ? {} : { segment: v }, { replace: true });
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                minHeight: 52,
                minWidth: 'auto',
                px: { xs: 1.5, sm: 2.5 },
              },
            }}
          >
            {SEGMENTS.map((s) => (
              <Tab
                key={s.key}
                value={s.key}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span style={{ whiteSpace: 'nowrap' }}>{s.label}</span>
                    <Chip
                      size="small"
                      label={badge(s.key)}
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        bgcolor:
                          segment === s.key
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.grey[500], 0.12),
                        color: segment === s.key ? 'primary.main' : 'text.secondary',
                      }}
                    />
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Enhanced Filters Toolbar */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Search Input */}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search name, phone, email, business…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#637381" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All statuses</em>
                </MenuItem>
                {FOLLOWUP_STATUSES.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Reason Blocker Filter */}
            <Grid size={{ xs: 6, sm: 3, md: 2.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Blocker / Objection"
                value={reasonCategory}
                onChange={(e) => {
                  setReasonCategory(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All reasons</em>
                </MenuItem>
                {FOLLOWUP_REASONS.map((r) => (
                  <MenuItem key={r.key} value={r.key}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Due Schedule Filter */}
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Schedule"
                value={due}
                onChange={(e) => {
                  setDue(e.target.value);
                  setPage(0);
                }}
              >
                {DUE_FILTERS.map((d) => (
                  <MenuItem key={d.key} value={d.key}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Sort Dropdown */}
            <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Sort By"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(0);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Reset Button */}
            {activeFiltersCount > 0 && (
              <Grid size={{ xs: 12, md: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<RotateCcw size={14} />}
                  onClick={handleResetFilters}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'error.main',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Reset ({activeFiltersCount})
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider />

        {/* Follow-ups Table */}
        <TableContainer
          sx={{
            overflowX: 'auto',
            // Theme pins the last column on phones; this table's actions are a
            // single ⋮ button, so keep it in normal flow (no sticky column).
            '& table tbody td:last-of-type:not([colspan]), & table thead th:last-of-type:not([colspan])': {
              position: 'static',
              boxShadow: 'none',
            },
          }}
        >
          <Table sx={{ minWidth: 920 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Lead / User</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Business</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Segment</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Next Touch</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Last Touch</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Blocker / Notes</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Joined</TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 700, width: 56 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={rowsPerPage} columns={10} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ p: 0 }}>
                    <EmptyState
                      title="No follow-up leads match your criteria"
                      description="Try adjusting your segment, status filters, or search terms."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((u) => {
                  const personName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                  const userName = personName || u.business_name || '—';
                  const seg = SEGMENT_COLORS[u.segment] || SEGMENT_COLORS.active;
                  const st = STATUS_COLORS[u.followup_status] || STATUS_COLORS.pending;
                  const fu = u.followup || {};

                  // Next follow up timing calculations
                  const nextIso = fu.next_follow_up_at ? fu.next_follow_up_at.slice(0, 10) : null;
                  const todayIso = new Date().toISOString().slice(0, 10);
                  const isOverdue = nextIso && nextIso < todayIso && !['converted', 'invalid', 'not_interested'].includes(u.followup_status);
                  const isDueToday = nextIso && nextIso === todayIso;

                  return (
                    <TableRow key={u._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {/* User Column */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                              fontSize: '0.875rem',
                              fontWeight: 700,
                            }}
                          >
                            {getInitials(userName)}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' },
                              }}
                              onClick={() => setSelected(u)}
                            >
                              {userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {u.email || '—'}
                            </Typography>
                            <Chip
                              size="small"
                              label={u.userType || 'Buyer'}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.6875rem', fontWeight: 600, mt: 0.25 }}
                            />
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Business Column */}
                      <TableCell sx={{ maxWidth: 170 }}>
                        {u.business_name ? (
                          <Box>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Building2 size={13} color="#f75d34" />
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {u.business_name}
                              </Typography>
                            </Stack>
                            {u.business_category && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {u.business_category}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                            {u.has_business ? 'Business created' : 'No business yet'}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Phone Column */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {u.phone || '—'}
                          </Typography>
                          {u.phone && (
                            <Tooltip title={copiedPhone === u.phone ? 'Copied!' : 'Copy phone'}>
                              <IconButton size="small" onClick={() => handleCopyPhone(u.phone)} sx={{ p: 0.25 }}>
                                {copiedPhone === u.phone ? (
                                  <Check size={13} color="#2e7d32" />
                                ) : (
                                  <Copy size={13} color="#919eab" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Segment Column */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={SEGMENT_LABEL[u.segment] || u.segment}
                          sx={{ bgcolor: seg.bg, color: seg.fg, fontWeight: 700, borderRadius: 1.5 }}
                        />
                      </TableCell>

                      {/* Status Column */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={labelOf(FOLLOWUP_STATUSES, u.followup_status)}
                          sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 700, borderRadius: 1.5 }}
                        />
                        {fu.contact_count > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {fu.contact_count} touch{fu.contact_count > 1 ? 'es' : ''}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Next Follow-up Column */}
                      <TableCell>
                        {nextIso ? (
                          <Box>
                            {isOverdue ? (
                              <Chip
                                size="small"
                                icon={<AlertTriangle size={12} />}
                                label={`Overdue: ${fmtDate(fu.next_follow_up_at)}`}
                                sx={{
                                  bgcolor: alpha(theme.palette.error.main, 0.12),
                                  color: 'error.main',
                                  fontWeight: 700,
                                  height: 22,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ) : isDueToday ? (
                              <Chip
                                size="small"
                                icon={<Clock size={12} />}
                                label="Due Today"
                                sx={{
                                  bgcolor: alpha(theme.palette.warning.main, 0.14),
                                  color: 'warning.dark',
                                  fontWeight: 700,
                                  height: 22,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {fmtDate(fu.next_follow_up_at)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Last Touch Column */}
                      <TableCell>
                        <Stack spacing={0.3}>
                          {fu.last_contacted_at ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {fmtDate(fu.last_contacted_at)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                via {fu.contact_method || 'call'} {fu.contact_count > 0 ? `(${fu.contact_count}x)` : ''}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              Never contacted
                            </Typography>
                          )}
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Eye size={12} color={fu.open_count > 0 ? '#637381' : '#b0b8c1'} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: fu.open_count > 0 ? 'text.secondary' : 'text.disabled',
                                fontSize: '0.6875rem',
                                fontWeight: fu.open_count > 0 ? 600 : 400,
                              }}
                            >
                              {fu.open_count ? `Opened ${fu.open_count}x` : 'Never opened'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      {/* Blocker & Notes Column */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        {fu.reason_category && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={labelOf(FOLLOWUP_REASONS, fu.reason_category)}
                            sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600, mb: 0.5 }}
                          />
                        )}
                        {fu.reason ? (
                          <Tooltip title={fu.reason}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ display: 'block', color: 'text.primary', maxWidth: 190 }}
                            >
                              {fu.reason}
                            </Typography>
                          </Tooltip>
                        ) : (
                          !fu.reason_category && (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )
                        )}
                      </TableCell>

                      {/* Joined Date */}
                      <TableCell>
                        <Typography variant="body2">{fmtDate(u.created_at)}</Typography>
                      </TableCell>

                      {/* Actions — all in one ⋮ menu */}
                      <TableCell align="right" sx={{ width: 56 }}>
                        <Tooltip title="Actions">
                          <IconButton
                            aria-label={`Actions for ${userName}`}
                            aria-haspopup="menu"
                            size="small"
                            onClick={(e) => setRowMenu({ anchor: e.currentTarget, user: u })}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Row actions menu */}
        <Menu
          anchorEl={rowMenu.anchor}
          open={Boolean(rowMenu.anchor)}
          onClose={closeRowMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 220, borderRadius: 2 } } }}
        >
          {(() => {
            const u = rowMenu.user;
            if (!u) return null;
            const fu = u.followup || {};
            const wa = waNumber(u.phone);
            const personName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
            const waContactName = personName || u.business_name || '';
            return [
              <MenuItem
                key="followup"
                onClick={() => { closeRowMenu(); setSelected(u); }}
                sx={{ color: ORANGE, fontWeight: 700 }}
              >
                <ListItemIcon sx={{ color: ORANGE }}><ClipboardList size={16} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>
                  Follow up {fu.open_count > 0 ? `(${fu.open_count})` : ''}
                </ListItemText>
              </MenuItem>,
              <MenuItem
                key="call"
                disabled={!u.phone}
                onClick={async () => {
                  closeRowMenu();
                  window.open(`tel:+${wa}`, '_self');
                  try {
                    await logFollowUpContact(u._id, 'call', 'Quick phone call touch');
                    load();
                  } catch {}
                }}
              >
                <ListItemIcon><Phone size={16} /></ListItemIcon>
                <ListItemText>Call user</ListItemText>
              </MenuItem>,
              <MenuItem
                key="wa"
                disabled={!wa}
                onClick={async () => {
                  closeRowMenu();
                  const greetingName = (u.first_name || '').trim() || (u.business_name || '').trim() || 'there';
                  const msg = `Hi ${greetingName}, this is from Reseller Bazzar. How can we help you complete your account setup?`;
                  try {
                    await logFollowUpContact(u._id, 'whatsapp', 'Quick touch via WHATSAPP');
                  } catch {}
                  navigate(`/whatsapp?phone=${encodeURIComponent(wa)}&name=${encodeURIComponent(waContactName)}&text=${encodeURIComponent(msg)}`);
                }}
              >
                <ListItemIcon sx={{ color: '#25D366' }}><MessageCircle size={16} /></ListItemIcon>
                <ListItemText>Chat on WhatsApp</ListItemText>
              </MenuItem>,
              <Divider key="div" />,
              <MenuItem
                key="remove"
                onClick={() => { closeRowMenu(); handleQuickRemove(u); }}
                sx={{ color: 'warning.dark' }}
              >
                <ListItemIcon sx={{ color: 'warning.dark' }}><UserX size={16} /></ListItemIcon>
                <ListItemText>Remove from follow-up</ListItemText>
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => { closeRowMenu(); setDeleteTargetUser(u); }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Trash2 size={16} /></ListItemIcon>
                <ListItemText>Delete user from database</ListItemText>
              </MenuItem>,
            ];
          })()}
        </Menu>

        <Divider />

        {/* Table Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 } }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Follow-up Details & Action Modal */}
      <FollowUpModal
        open={!!selected}
        user={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
      />

      {/* WhatsApp Automation Quick Run Dialog */}
      <Dialog
        open={waModalOpen}
        onClose={() => !waRunning && setWaModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: alpha('#25D366', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageCircle size={22} color="#128C7E" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  WhatsApp Follow-up Automation
                </Typography>
                <Chip
                  size="small"
                  label={waAutoStatus?.is_live_send ? 'Meta Live Mode' : 'Simulated / Safe Mode'}
                  color={waAutoStatus?.is_live_send ? 'success' : 'info'}
                  sx={{ fontWeight: 700, height: 22 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Auto-dispatch templates to unverified users, pending businesses, and empty catalogs
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {/* Live counts chips */}
            {waAutoStatus?.counts && (
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 1, textTransform: 'uppercase', color: 'text.secondary' }}>
                  Live Eligible Audience
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant={waSegment === 'all' ? 'filled' : 'outlined'}
                    color="primary"
                    label={`All (${waAutoStatus.counts.total})`}
                    onClick={() => setWaSegment('all')}
                    sx={{ fontWeight: 700, cursor: 'pointer' }}
                  />
                  <Chip
                    size="small"
                    variant={waSegment === 'unverified' ? 'filled' : 'outlined'}
                    color="error"
                    label={`Unverified (${waAutoStatus.counts.unverified})`}
                    onClick={() => setWaSegment('unverified')}
                    sx={{ fontWeight: 700, cursor: 'pointer' }}
                  />
                  <Chip
                    size="small"
                    variant={waSegment === 'business_pending' ? 'filled' : 'outlined'}
                    color="warning"
                    label={`Pending Business (${waAutoStatus.counts.business_pending})`}
                    onClick={() => setWaSegment('business_pending')}
                    sx={{ fontWeight: 700, cursor: 'pointer' }}
                  />
                  <Chip
                    size="small"
                    variant={waSegment === 'product_pending' ? 'filled' : 'outlined'}
                    color="info"
                    label={`Pending Products (${waAutoStatus.counts.product_pending})`}
                    onClick={() => setWaSegment('product_pending')}
                    sx={{ fontWeight: 700, cursor: 'pointer' }}
                  />
                </Stack>
              </Box>
            )}

            <TextField
              select
              fullWidth
              size="small"
              label="Selected Target Segment"
              value={waSegment}
              onChange={(e) => setWaSegment(e.target.value)}
              disabled={waRunning}
            >
              <MenuItem value="all">All Pending Onboarding Users</MenuItem>
              <MenuItem value="unverified">Unverified Signups Only</MenuItem>
              <MenuItem value="business_pending">Pending Business Setup Only</MenuItem>
              <MenuItem value="product_pending">Pending Product Addition Only</MenuItem>
            </TextField>

            <TextField
              type="number"
              fullWidth
              size="small"
              label="Batch Limit (Max Contacts to Process)"
              value={waLimit}
              onChange={(e) => setWaLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              disabled={waRunning}
              helperText="Enforces anti-spam cooldown, 24h fatigue window, and 3-nudge limit."
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={waDryRun}
                  onChange={(e) => setWaDryRun(e.target.checked)}
                  disabled={waRunning}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Run in Dry-Run / Test Mode
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check guardrails and resolve recipients without sending live WhatsApp messages.
                  </Typography>
                </Box>
              }
            />

            {waRunReport && (
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'success.dark', mb: 1 }}>
                  Execution Result (Run #{waRunReport.run_id ? waRunReport.run_id.slice(-6) : 'DONE'})
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Evaluated</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{waRunReport.total_candidates}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="success.main">Processed</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>
                      {waRunReport.sent + waRunReport.simulated}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="warning.main">Skipped</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'warning.main' }}>{waRunReport.skipped}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="error.main">Errors</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>{waRunReport.failed}</Typography>
                  </Grid>
                </Grid>
                {waRunReport.skip_reasons && Object.keys(waRunReport.skip_reasons).length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #ddd' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Guardrail skips: </Typography>
                    {Object.entries(waRunReport.skip_reasons).map(([k, v]) => (
                      <Chip key={k} size="small" label={`${k}: ${v}`} sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
                    ))}
                  </Box>
                )}
              </Card>
            )}

            <Button
              variant="text"
              size="small"
              endIcon={<ArrowRight size={14} />}
              onClick={() => {
                setWaModalOpen(false);
                navigate('/whatsapp?tab=automations');
              }}
              sx={{ alignSelf: 'flex-start', fontWeight: 600, textTransform: 'none' }}
            >
              Configure Templates & Guardrail Rules in WhatsApp Automation &rarr;
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setWaModalOpen(false)}
            disabled={waRunning}
            color="inherit"
          >
            {waRunReport ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteWaRun}
            disabled={waRunning}
            startIcon={waRunning ? <CircularProgress size={16} color="inherit" /> : <Play size={16} />}
            sx={{ fontWeight: 700, px: 3 }}
          >
            {waRunning ? 'Processing...' : (waDryRun ? 'Start Dry Run' : 'Execute Follow-ups')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTargetUser)}
        onClose={() => !deletingUser && setDeleteTargetUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={20} color="#d32f2f" />
          Delete User from Database
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to permanently delete this user from the <b>user table</b>?
          </Typography>
          {deleteTargetUser && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.neutral', borderRadius: 1.5, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {`${deleteTargetUser.first_name || ''} ${deleteTargetUser.last_name || ''}`.trim() || deleteTargetUser.business_name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Phone: {deleteTargetUser.phone || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Segment: {SEGMENT_LABEL[deleteTargetUser.segment] || deleteTargetUser.segment}
              </Typography>
            </Paper>
          )}
          <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
            This will permanently remove the user from the user table along with any follow-up records. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDeleteTargetUser(null)} color="inherit" disabled={deletingUser}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteUser}
            disabled={deletingUser}
            startIcon={deletingUser ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
          >
            {deletingUser ? 'Deleting…' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
